import { BrandsTable } from 'components';
import CatFiveAffinitiesTable from 'components/DataVis/AffinitiesTable/CatFiveAffinitiesTable';
import CatFiveAffinityCategoriesTable from 'components/DataVis/CatFiveAffinityCategoriesTable';
import CategoryDescription from 'components/CategoryDescription';
import { DATA_SOURCES } from '@/config/constants';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityFlavors from '../shared/entity/EntityFlavors';
import EntityHeroCard from '../shared/entity/EntityHeroCard';
import EntityPage from '../shared/entity/EntityPage';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import GoogleBreadCrumbs from 'components/Breadcrumbs/GoogleBreadCrumbs';
import { GoogleChartCard } from '../shared/entity/chartCards';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import { hasFlavorsTab, isTikTokMarket } from '../shared/entityPredicates';
import MarketsSection from 'components/DataVis/MarketsSection';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopKeywordsSection } from 'components/TopKeywordsSection';
import useGoogleEntity from '../shared/entity/useGoogleEntity';
import { useCurrentCountry } from 'utils/hooks';
import { useTrackCategoryView } from 'utils/hooks/useTrackEntityView';
import { views } from '@/copy';

// Cat5 reuses the cat3 section copy.
const ct = views.categoryThree.sections;

const METRICS = [GOOGLE_METADATA_BY_KEY.brandsVolume];

function GoogleCategoryFivePage({ categoryId, controller, tabs }) {
    const country = useCurrentCountry();
    const page = useGoogleEntity({
        entityType: 'categories',
        entityId: categoryId,
        country,
        controller,
        coverage: 'hint',
    });
    const { details, entityQuery, name, row } = page;

    const healthStats = row?.healthStats;

    useTrackCategoryView(DATA_SOURCES.GOOGLE, {
        id: categoryId,
        categoryName: name,
        countryName: country?.name || null,
    });

    const skipPrediction = isTikTokMarket(page);

    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={GoogleBreadCrumbs} />}
            sourceTabs={tabs}
            title={{ name, isLoading: entityQuery.isLoading }}
            actions={
                <ToggleWatch
                    id={categoryId}
                    data={{ name, type: 'category' }}
                />
            }
            sections={
                <EntityTopTerms
                    as={TopKeywordsSection}
                    exportType="search-query"
                    highlighted
                    paginateBy="categories"
                    dated={false}
                    controller={controller}
                />
            }
            tabs={
                <EntityTabs>
                    <EntityTab name="Related Searches">
                        <CatFiveAffinitiesTable
                            icon="affinity"
                            title={ct.affinities.title}
                            subtitle={ct.affinities.subtitle}
                            tooltipKey="affinitiesTitleHelp"
                            tooltipExample="affinitiesExample"
                            countryId={country ? country.id : null}
                            categoryId={categoryId || null}
                            controller={controller}
                            name={name}
                            filename={page.exportName('related-search', {
                                entity: name || 'trend',
                            })}
                            sortParamKey="relatedSort"
                        />
                    </EntityTab>
                    <EntityTab name="Brands">
                        <BrandsTable
                            title={ct.brands.title}
                            subtitle={ct.brands.subtitle}
                            tooltipKey="brandsTitleHelp"
                            tooltipExample="brandsExample"
                            countryId={country ? country.id : null}
                            categoryId={categoryId}
                            controller={controller}
                            name={name}
                            filename={page.exportName('brand', {
                                entity: name || 'trend',
                            })}
                            sortParamKey="brandSort"
                        />
                    </EntityTab>
                    {hasFlavorsTab(page) && (
                        <EntityTab name="Flavors">
                            <EntityFlavors
                                dataSource="google"
                                subject="flavorsSearched"
                                exportEntity={name || 'trend'}
                                dated={false}
                            />
                        </EntityTab>
                    )}
                    <EntityTab name="Industries">
                        <div>
                            <CatFiveAffinityCategoriesTable
                                categoryId={categoryId}
                                countryId={country ? country.id : null}
                                categoryName={name}
                            />
                        </div>
                    </EntityTab>
                    <EntityTab name="Global">
                        <MarketsSection
                            controller={controller}
                            categoryId={categoryId}
                            name={name}
                            hierarchy_hint={page.hierarchyHint}
                        />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <div className="flex flex-col gap-3">
                <EntityHeroCard
                    healthStats={healthStats}
                    showPredicted={!skipPrediction}
                />
                <CategoryDescription
                    categoryId={categoryId}
                    categoryName={name}
                    flavors={details?.flavors}
                />
                <GoogleChartCard
                    metricList={METRICS}
                    healthStats={healthStats}
                    skipPrediction={skipPrediction}
                />
            </div>
        </EntityPage>
    );
}

GoogleCategoryFivePage.displayName = 'GoogleCategoryFive';

export default GoogleCategoryFivePage;
